import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../lib/api';

interface DailyQuestTemplate {
  id: string;
  name: string;
  description: string;
  event_type: string;
  target_count: number;
  competency_id: string;
  xp: number;
  // M5.8: parallel coin credit per template. The web
  // exposes it as a per-template input next to XP; the
  // api (apps/api/src/api/daily-quests.routes.ts) reads
  // and writes the column. Default 0 = no coins.
  coins: number;
  enabled: boolean;
}
interface EventType { id: string; description: string; }
interface Competency { id: string; name: string; dimension: string; }

// Admin pool of daily quest templates. The system picks 3
// competency-diverse templates per user per day (lazy) and observed
// events auto-increment progress. See ADR-0012.
export function DailyQuestTemplatesPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['daily-quest-templates'],
    queryFn: () => api<{ templates: DailyQuestTemplate[] }>('/api/admin/daily-quest-templates'),
  });
  const eventTypes = useQuery({ queryKey: ['event-types'], queryFn: () => api<{ event_types: EventType[] }>('/api/admin/event-types') });
  const competencies = useQuery({ queryKey: ['competencies'], queryFn: () => api<{ competencies: Competency[] }>('/api/competencies') });

  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api<{ template: DailyQuestTemplate }>('/api/admin/daily-quest-templates', { method: 'POST', body: form! }),
    onSuccess: () => { setCreating(false); setForm(null); qc.invalidateQueries({ queryKey: ['daily-quest-templates'] }); },
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<DailyQuestTemplate> }) =>
      api<{ template: DailyQuestTemplate }>(`/api/admin/daily-quest-templates/${id}`, { method: 'PATCH', body }),
    onSuccess: () => { setEditing(null); setForm(null); qc.invalidateQueries({ queryKey: ['daily-quest-templates'] }); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/admin/daily-quest-templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['daily-quest-templates'] }),
  });

  const startCreate = () => {
    setCreating(true); setEditing(null); setErr(null);
    setForm(blankForm());
  };
  const startEdit = (t: DailyQuestTemplate) => {
    setEditing(t.id); setCreating(false); setErr(null);
    setForm({
      id: t.id, name: t.name, description: t.description, event_type: t.event_type,
      target_count: t.target_count, competency_id: t.competency_id,
      xp: t.xp, coins: t.coins ?? 0, enabled: t.enabled,
    });
  };

  const save = async () => {
    if (!form) return;
    setErr(null);
    if (!form.id.trim() || !form.name.trim() || !form.event_type || !form.competency_id) {
      setErr('Id, name, event type, and competency are required.'); return;
    }
    try {
      if (creating) {
        await create.mutateAsync();
      } else if (editing) {
        await update.mutateAsync({
          id: editing,
          body: {
            name: form.name, description: form.description, event_type: form.event_type,
            target_count: form.target_count, competency_id: form.competency_id,
            xp: form.xp, coins: form.coins, enabled: form.enabled,
          },
        });
      }
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Save failed.');
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Daily quest templates</h1>
          <p className="text-ink-muted">
            The pool the system picks from. Each user gets 3 competency-diverse templates per day
            (random). Observed events matching a template's event type auto-increment progress —
            no manual claim. Complete all 3 in a day for a perfect-day bonus.
          </p>
        </div>
        <button className="btn-primary" onClick={startCreate} disabled={creating || editing !== null}>
          + Add template
        </button>
      </header>

      {error ? <p className="text-sm text-accent-danger">Failed to load templates.</p> : null}
      {err ? <p className="text-sm text-accent-danger" role="alert">{err}</p> : null}

      {form ? (
        <div className="card space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">
            {creating ? 'New template' : 'Edit template'}
          </h2>
          <div className="grid gap-3 sm:grid-cols-4">
            <div className="space-y-1">
              <label className="label">Id</label>
              <input className="input font-mono" value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })} disabled={!!editing} placeholder="dqt_review_pr" />
            </div>
            <div className="space-y-1">
              <label className="label">Name</label>
              <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Review a PR" />
            </div>
            <div className="space-y-1">
              <label className="label">XP</label>
              <input className="input" type="number" min="0" value={form.xp} onChange={(e) => setForm({ ...form, xp: Number(e.target.value) })} />
            </div>
            {/* M5.8: parallel coin credit. Same int constraint
                as the api's zod schema (0..10000). 0 means
                "no coins" (default, matches legacy behaviour). */}
            <div className="space-y-1">
              <label className="label">🪙 Coins</label>
              <input
                className="input"
                type="number"
                min="0"
                max="10000"
                value={form.coins}
                onChange={(e) => setForm({ ...form, coins: Number(e.target.value) })}
                title="Coins credited when this quest is completed (M5.8)"
              />
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <label className="label">Event type</label>
              <select className="input font-mono" value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })}>
                <option value="">— select —</option>
                {(eventTypes.data?.event_types ?? []).map((t) => <option key={t.id} value={t.id}>{t.id}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="label">Target count</label>
              <input className="input" type="number" min="1" value={form.target_count} onChange={(e) => setForm({ ...form, target_count: Number(e.target.value) })} />
            </div>
            <div className="space-y-1">
              <label className="label">Competency</label>
              <select className="input" value={form.competency_id} onChange={(e) => setForm({ ...form, competency_id: e.target.value })}>
                <option value="">— select —</option>
                {(competencies.data?.competencies ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-1">
            <label className="label">Description</label>
            <input className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Submit 1 PR review today." />
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="h-4 w-4" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
              Enabled
            </label>
            <div className="flex gap-2">
              <button className="btn" onClick={() => { setForm(null); setEditing(null); setCreating(false); }}>Cancel</button>
              <button className="btn-primary" onClick={save} disabled={create.isPending || update.isPending}>
                {create.isPending || update.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-ink-muted">
            <tr>
              <th className="py-2">Name</th>
              <th>Event type</th>
              <th>Target</th>
              <th>Competency</th>
              <th>XP</th>
              <th>🪙</th>
              <th>Enabled</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(data?.templates ?? []).map((t) => {
              const comp = (competencies.data?.competencies ?? []).find((c) => c.id === t.competency_id);
              return (
                <tr key={t.id} className="border-t border-white/5">
                  <td className="py-2 font-medium">{t.name}<span className="block font-mono text-xs text-ink-dim">{t.id}</span></td>
                  <td className="font-mono text-xs">{t.event_type}</td>
                  <td className="tabular-nums">{t.target_count}</td>
                  <td className="text-ink-muted">{comp?.name ?? t.competency_id}</td>
                  <td className="tabular-nums">{t.xp}</td>
                  <td className="tabular-nums">{t.coins || <span className="text-ink-dim">—</span>}</td>
                  <td>{t.enabled ? '✓' : <span className="text-ink-dim">off</span>}</td>
                  <td className="space-x-1 text-right">
                    <button className="btn" onClick={() => startEdit(t)} disabled={!!editing || !!creating}>Edit</button>
                    <button className="btn-danger" onClick={() => remove.mutate(t.id)}>×</button>
                  </td>
                </tr>
              );
            })}
            {!isLoading && !(data?.templates ?? []).length ? (
              <tr><td colSpan={8} className="py-4 text-center text-ink-muted">No templates yet.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function blankForm(): FormState {
  return { id: '', name: '', description: '', event_type: '', target_count: 1, competency_id: '', xp: 5, coins: 0, enabled: true };
}
interface FormState {
  id: string; name: string; description: string; event_type: string;
  target_count: number; competency_id: string; xp: number;
  // M5.8: parallel coin credit. Default 0 in blankForm().
  coins: number; enabled: boolean;
}